import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatError } from "@/lib/api";

const EMPTY = { name: "", email: "", company: "", message: "", website: "" };

export function ContactDialog({ handle, name }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/public/learners/${handle}/contact`, f); setDone(true); setF(EMPTY); } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDone(false); }}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="showcase-contact-btn" className="rounded-full bg-aqua-solid hover:bg-aqua-solid-hover"><Send className="mr-1.5 h-3.5 w-3.5" />Contact this learner</Button>
      </DialogTrigger>
      <DialogContent data-testid="contact-dialog" className="w-[calc(100%-2rem)] rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-ink">Contact {name}</DialogTitle>
          <DialogDescription>IDDCR forwards your message by email. {name.split(" ")[0]}'s email stays private; they'll reply to you directly if interested.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div data-testid="contact-success" className="flex flex-col items-start gap-2 py-4">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <p className="font-semibold text-ink">Message sent</p>
            <p className="text-sm text-ink-2">{name.split(" ")[0]} will reply to your email if interested.</p>
          </div>
        ) : (
          <form onSubmit={send} className="flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input data-testid="contact-name-input" required minLength={2} maxLength={80} placeholder="Your name" value={f.name} onChange={set("name")} />
              <Input data-testid="contact-email-input" required type="email" placeholder="Work email" value={f.email} onChange={set("email")} />
            </div>
            <Input data-testid="contact-company-input" maxLength={120} placeholder="Company (optional)" value={f.company} onChange={set("company")} />
            <input tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={f.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" />
            <Textarea data-testid="contact-message-input" required minLength={10} maxLength={1000} rows={5} placeholder="Your message: the role, why you're reaching out, next steps..." value={f.message} onChange={set("message")} />
            <div className="flex items-center justify-between">
              <span data-testid="contact-message-count" className="text-xs text-ink-3">{f.message.length}/1000</span>
              <Button type="submit" data-testid="contact-submit-btn" disabled={busy} className="rounded-full bg-solid">{busy ? "Sending..." : "Send message"}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
